/**
 * Advanced logic
 * @module videoPlayer/helpers/advancedLogic
 */
import jsonLogic from 'json-logic-js';

import { EVENT_WILL_SET_VARIANT } from '../video';
import { dispatchContextUpdate } from '../events';
import { getCurrentPlayer } from '../helpers';

export function setupAdvancedLogicContext(players) {
  let initialContext = window.wrpVariants.jsonlogic_project_variables || {};

  const aLContext = new AdvancedLogicContext(initialContext, players);

  players.forEach((player) => {
    if (player.variant) {
      aLContext.initialContextForVariant(player.variant);
    }

    player.on(EVENT_WILL_SET_VARIANT, (e, { variant }) => {
      aLContext.initialContextForVariant(variant);
    });

    player.on('ended', () => {
      if (!player.variant.behaviours_jsonlogic) {
        return;
      }
      aLContext.processBehaviours(player.variant.behaviours_jsonlogic.finish);
    });

    // player.on('sw.variant.start', (e) => {
    //   // console.log('sw.variant.start');
    //   // console.log(e.detail.variant);
    // });

    player.on('sw.variant.progress', (e) => {
      const variant = e.detail.variant,
        behavioursJsonLogic = variant.behaviours_jsonlogic;
      if (!behavioursJsonLogic) {
        return;
      }

      const playbackTime = Math.floor(e.detail.current);
      aLContext.processSpecificTimeBehaviours({
        behavioursJsonLogic,
        playbackTime,
      });
    });
  });

  return aLContext;
}

function AdvancedLogicContext(initialContext, players) {
  this.currentContext = initialContext;
  this.lastPointInTimeBehaviour = null;
  this.players = players;
  console.info('[AdvancedLogicContext] initialContext', this.currentContext);
  dispatchContextUpdate(this.players[0], this.currentContext);
}

AdvancedLogicContext.prototype.getLinkedVariant = function ({ choice }) {
  const context = this.currentContext;
  // TODO support more than one?
  const logicBlock = choice.advanced_logic[0];
  const outcome = jsonLogic.apply(logicBlock, context);
  console.info('[AdvancedLogicContext] getLinkedVariant: logicBlock', logicBlock);
  console.info('[AdvancedLogicContext] getLinkedVariant: outcome', outcome);
  if (outcome && outcome.behaviours_jsonlogic) {
    this.processBehaviours(outcome.behaviours_jsonlogic);
  }
  return outcome;
};

AdvancedLogicContext.prototype.initialContextForVariant = function (newVariant) {
  if (!newVariant.behaviours_jsonlogic) {
    return;
  }
  this.processBehaviours(newVariant.behaviours_jsonlogic.start);
};

AdvancedLogicContext.prototype.processSpecificTimeBehaviours = function ({ behavioursJsonLogic, playbackTime }) {
  if (playbackTime == this.lastPointInTimeBehaviour) {
    // these behaviours have run already
    return;
  }
  if (playbackTime < this.lastPointInTimeBehaviour) {
    // allow behaviours to run again if user seeks through the video
    this.lastPointInTimeBehaviour = playbackTime;
  }

  const behaviours = behavioursJsonLogic.specific_times[playbackTime];

  if (!behaviours) {
    return;
  }

  this.lastPointInTimeBehaviour = playbackTime;
  this.processBehaviours(behaviours);
};

AdvancedLogicContext.prototype.processBehaviours = function (behaviours) {
  this.processSetBehaviours(behaviours.set);
  this.processChangeBehaviours(behaviours.change);
  this.processAddUpBehaviours(behaviours.add_up);
  this.processToggleBehaviours(behaviours.toggle);
};

AdvancedLogicContext.prototype.processSetBehaviours = function (setBehaviours) {
  if (!setBehaviours) {
    return;
  }

  const updatedVars = setBehaviours.reduce(function (memo, behaviour) {
    behaviour.variables.forEach(function (varName) {
      memo[varName] = behaviour.value;
    });
    return memo;
  }, {});

  this.updateContext(updatedVars);
};

AdvancedLogicContext.prototype.processAddUpBehaviours = function (addUpBehaviours) {
  if (!addUpBehaviours) {
    return;
  }

  const self = this;
  const updatedVars = addUpBehaviours.reduce(function (memo, addUpBehaviour) {
    const newValue = self.getTargetVarValue(addUpBehaviour);
    memo[addUpBehaviour.target_var] = newValue;
    return memo;
  }, {});
  this.updateContext(updatedVars);
};

/**
 * NB. add up only supporting simple number adding for now
 */
AdvancedLogicContext.prototype.getTargetVarValue = function (behaviour) {
  const self = this;
  return behaviour.variables.reduce(function (memo, varName) {
    const currentValue = self.currentContext[varName];
    return memo + currentValue;
  }, 0);
};

AdvancedLogicContext.prototype.getChangeVarNewValue = function (behaviour, varName) {
  const currentValue = this.currentContext[varName];
  if (Array.isArray(currentValue)) {
    return currentValue.concat(behaviour.value);
  } else {
    return currentValue + behaviour.value;
  }
};

AdvancedLogicContext.prototype.processChangeBehaviours = function (changeBehaviours) {
  if (!changeBehaviours) {
    return;
  }
  const self = this;

  const updatedVars = changeBehaviours.reduce(function (memo, changeBehaviour) {
    changeBehaviour.variables.forEach(function (varName) {
      const newValue = self.getChangeVarNewValue(changeBehaviour, varName);
      memo[varName] = newValue;
    });
    return memo;
  }, {});

  this.updateContext(updatedVars);
};

AdvancedLogicContext.prototype.processToggleBehaviours = function (toggleBehaviours) {
  if (!toggleBehaviours) {
    return;
  }

  const self = this;
  const updatedVars = toggleBehaviours.reduce(function (memo, toggleBehaviour) {
    toggleBehaviour.variables.forEach(function (varName) {
      const currentValue = self.currentContext[varName];

      // Toggle between 'True' and 'False' because the form capitalises
      if (currentValue === 'True') {
        memo[varName] = 'False';
      } else if (currentValue === 'False') {
        memo[varName] = 'True';
      }
      // Toggle between 'true' and 'false' for times when booleans have been set programatically
      else if (currentValue === 'true') {
        memo[varName] = 'false';
      } else if (currentValue === 'false') {
        memo[varName] = 'true';
      }
      // Ignore any other values
    });
    return memo;
  }, {});

  this.updateContext(updatedVars);
};

/**
 * expect to receive
 * @param {*} toApply expects a JSON object with variables and their
 *   new values. example: { contextVar: newValue }
 */
AdvancedLogicContext.prototype.updateContext = function (toApply) {
  this.currentContext = Object.assign(this.currentContext, toApply);
  console.info('[AdvancedLogicContext] updateContext', this.currentContext);
  dispatchContextUpdate(getCurrentPlayer(this.players), this.currentContext);
};

AdvancedLogicContext.prototype.runBehavioursBeforeClick = function (choice) {
  if (!choice.behaviour_blocks_before_click) {
    return;
  }
  choice.behaviour_blocks_before_click.forEach((behaviourBlock) => {
    this.processBehaviours(behaviourBlock);
  });
};

AdvancedLogicContext.prototype.runBehavioursOnClick = function (choice) {
  if (!choice.behaviour_blocks_before_click) {
    return;
  }
  choice.behaviour_blocks_on_click.forEach((behaviourBlock) => {
    this.processBehaviours(behaviourBlock);
  });
};
